import React, { useState, useMemo } from "react";
import theme from "../../data/theme";
import LiveBoardSetup from "./LiveBoardSetup";
import LiveBoardEntry from "./LiveBoardEntry";
import RoomRead from "./RoomRead";
import RosterBoard from "./RosterBoard";
import { SyncAlarm } from "./ShareBar";
import TierCliff from "./TierCliff";
import LiveCheatSheet from "./LiveCheatSheet";
import NominationBlock, { useSoldFlash, NOMINATION_RESERVE } from "./NominationBlock";
import TeamNamesEditor from "./TeamNamesEditor";
import { useBoardScale, isTvMode, BoardScaleStepper } from "./BoardScale";
import { useClipsEnabled } from "./ClipToggle";
import BoardMenu from "./BoardMenu";
import NominationFlash from "./NominationFlash";
import DraftCountdown from "./DraftCountdown";
import DraftLog from "./DraftLog";
import BoardsElsewhere from "./BoardsElsewhere";
import ConfirmDestroy from "./ConfirmDestroy";
import { boardToCsv, otherBoardsInProgress, nextOpenSeat, prevOpenSeat } from "../../utils/liveBoard";
import { boardFocus } from "../../utils/boardFocus";
import { slugify } from "../../utils/downloadJson";
import SaleEditor from "./SaleEditor";

/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE ADVISORY LAYER IS OFF — A HIDE, NOT A FIX (Aug 26).
 *
 * Two panels tell the operator what to DO rather than what happened:
 *   · RoomRead     — the room's running price temperature, in the entry bar
 *   · TierCliff    — BEST LEFT, and the drop to the next man
 *   · STILL NEEDED — the positional roll-up under the board
 *
 * 🔴 STILL NEEDED JOINED THEM Aug 26, AND IT IS THE ODD ONE OUT ON PAPER: it
 * is a pure count off the rosters, so unlike the other two it cannot be wrong.
 * It is here because of what it is FOR. watchPage.test.js already took it off
 * the spectator feed under rule 2 — "the board does not do the derivation for
 * you" — on the grounds that rolling twelve rosters into one line hands over a
 * read nobody at a real auction could perform. That reasoning does not stop
 * being true because the reader is the operator: it is still a derived read on
 * the room, offered at the moment somebody acts on it, and it belongs with the
 * other two behind one switch rather than in a category of its own.
 *
 * The other two rest on board-level calibration that is known wrong in the direction
 * that matters here: the board runs HOT against real rooms at the top, and the
 * tier bands under BEST LEFT are Underdog's, not a read of this league. So the
 * advice is confidently wrong at exactly the moment somebody would act on it,
 * with eleven people waiting. The root cause is upstream of this file and is
 * not fixable from it, which is why this is a switch and not a rewrite.
 *
 * ⚠️ THE COMPONENTS AND THEIR TESTS STAY. Flipping this to true restores both
 * panels; nothing about them is deleted, and watchPage.test.js still asserts
 * neither is importable into the spectator tree.
 *
 * ⚠️ WHAT IS NOT ADVICE STAYS ON SCREEN, and none of it moves: saleWarnings,
 * Max $ on the team cards, every budget, and PLAYERS LEFT. Those report the
 * board; they do not tell anyone what to bid. `src/utils/roomTemp.js` and the
 * mock draft's inflation index are untouched.
 * ══════════════════════════════════════════════════════════════════════
 */
export const LIVE_BOARD_ADVISORY = false;

/**
 * LiveBoard — the container.
 *
 * LAYOUT: the board starts at the very top of the page and the entry row is
 * PINNED TO THE BOTTOM EDGE OF THE VIEWPORT — position: fixed, not merely last
 * in the DOM.
 *
 * 🔴 WHY FIXED AND NOT IN-FLOW. Twelve teams x sixteen slots is taller than any
 * screen. An in-flow entry row therefore scrolls off the moment you look at the
 * board, and every single pick would begin with a scroll to the bottom. Hands
 * stay at the bottom edge; the board fills everything above them.
 *
 * Two consequences that are easy to miss and both break entry silently:
 *   1. The suggestion menus must open UPWARD (see LiveBoardEntry's `dropUp`) —
 *      a menu below a bottom-pinned field renders off-screen with nothing to
 *      scroll to.
 *   2. The page needs bottom padding the height of the bar, or the last roster
 *      card sits underneath it permanently and cannot be reached.
 *
 * The room read rides in the same bar, in its one-line form.
 */
// Vertical space the pinned bar occupies, reserved as page padding so the last
// roster card is never stranded underneath it. Generous on purpose: the bar
// grows a row when a warning or the "not saving" banner appears.
const BAR_RESERVE = 150;

/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE POINTER MOVES FROM WHERE THE HANDS ALREADY ARE. Ben runs 160 picks
 * with his hands at the bottom edge of the screen, so a correction to whose
 * turn it is has to be reachable there — not in a menu he has to open, and not
 * on a roster card he has to find in a 5x5.
 *
 * 🔴 LABELLED WITH THE SEAT IT WOULD MOVE TO, NEVER A BARE ARROW. At a live
 * auction an unlabelled arrow is a guess, and a guess that costs a click to
 * undo is worse than no control. The names come from the SAME helpers the
 * rotation writes with, so the label cannot promise a seat the step will not
 * land on.
 *
 * 🔴 tabIndex={-1}, AND THAT IS LOAD-BEARING. The pinned bar is last in the DOM
 * so it is last in tab order, and entry is keyboard-only at a sub-three-second
 * budget: tab must run player -> price -> team and nothing else. A focusable
 * button between them costs a keystroke on every one of 160 picks.
 *
 * ⚠️ THESE ARE NOT `setNominator`. They SKIP seats that are done — see the
 * hook. The board menu keeps the exact, unfiltered version.
 *
 * ⚠️ THE ROW LABEL IS "Turn", NOT "Up now". "Up now" is RosterBoard's
 * visually-hidden label for the lit card, and nominationBlock.test.js asserts
 * that string is present exactly when the baton is on a card. A second element
 * answering to it makes those queries ambiguous — the mark and its remote
 * control must not share a name.
 * ══════════════════════════════════════════════════════════════════════
 */
function NominatorStep({ board, onStep }) {
  if (!board || !onStep) return null;
  const names = board.teamNames || [];
  const back = prevOpenSeat(board, board.nominatorIndex);
  const fwd = nextOpenSeat(board, board.nominatorIndex);

  const btn = (enabled) => ({
    background: "transparent",
    border: `1px solid ${enabled ? theme.colors.border.default : theme.colors.border.subtle}`,
    borderRadius: theme.radius.md,
    color: enabled ? theme.colors.text.muted : theme.colors.border.default,
    fontFamily: "inherit", fontSize: theme.fontSize.caption, fontWeight: theme.fontWeight.bold,
    padding: "4px 10px", cursor: enabled ? "pointer" : "default",
    whiteSpace: "nowrap", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis",
  });

  return (
    <div data-testid="nominator-step" style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
      <span style={{
        fontSize: theme.fontSize.nano, textTransform: "uppercase", letterSpacing: "0.08em",
        color: theme.colors.text.faint, fontWeight: theme.fontWeight.bold,
      }}>Turn</span>
      <button
        type="button" tabIndex={-1}
        data-testid="step-nominator-back"
        onClick={() => onStep(-1)}
        disabled={back < 0}
        aria-label={back >= 0 ? `Step whose turn back to ${names[back]}` : "Step whose turn back"}
        // 🔴 A TOOLTIP, BECAUSE THE LABEL IS THE SEAT NAME. The buttons read
        // "◀ Mike" / "Dana ▶", which says where they land and not what they
        // are FOR — and what they are for (somebody left, move past them) is
        // the thing an operator is looking for at pick 90. The aria-label
        // cannot carry it: a screen reader needs the destination.
        title="Back to the previous nominator"
        style={btn(back >= 0)}
      >{back >= 0 ? `◀ ${names[back]}` : "◀"}</button>
      <button
        type="button" tabIndex={-1}
        data-testid="step-nominator-fwd"
        onClick={() => onStep(1)}
        disabled={fwd < 0}
        aria-label={fwd >= 0 ? `Step whose turn on to ${names[fwd]}` : "Step whose turn on"}
        title="Skip to the next nominator"
        style={btn(fwd >= 0)}
      >{fwd >= 0 ? `${names[fwd]} ▶` : "▶"}</button>
    </div>
  );
}

/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 `?setup=1` IS A REQUEST, NOT A ROUTE.
 *
 * "Set up your own" on the sample board, and OPEN on the landing card, are
 * clicked by somebody who has decided to run a draft. Landing them inside a
 * half-finished board from three weeks ago answers a question they did not
 * ask — and on the landing card it is worse, because that card is the first
 * thing a stranger sees and it must not open somebody else's mid-auction
 * screen.
 *
 * ⚠️ A BARE /live IS UNCHANGED AND MUST STAY THAT WAY. That address exists
 * because closing the operator tab on draft night left no way back in; sending
 * it to a setup form with eleven people waiting would undo exactly what it was
 * added for. The two cases are told apart by the marker, never by guessing at
 * the board's state.
 *
 * The board is NOT hidden when this fires — a banner offers it in one click,
 * and starting a new one still goes through the overwrite confirmation.
 * ══════════════════════════════════════════════════════════════════════
 */
export const SETUP_PARAM = "setup";

function setupRequestedInUrl() {
  try {
    return new URLSearchParams(window.location.search).get(SETUP_PARAM) === "1";
  } catch {
    return false;                     // no URL API: behave like a bare /live
  }
}

/**
 * `onLeave` is the only way off this surface once chunk 5 hides the app header
 * at TV, and it is optional so the component still renders standalone in tests.
 */
export default function LiveBoard({
  live, dynamicPrices, activeLeagueName, priceSettings, onLeave = null,
  savedLeagues = null, onSwitchLeague = null,
  // 🔴 DEFAULTS TO THE MODULE CONSTANT, WHICH STAYS THE ONE SOURCE OF TRUTH.
  // The prop exists so a test can render the ON arm for real rather than
  // asserting a branch it never executes — no caller passes it.
  advisory = LIVE_BOARD_ADVISORY,
}) {
  const { board, totals, sold, liveTeams, rosterSlots, needs,
    startBoard, commitSale, editSale, deleteSale, setUserTeam, renameTeam, resetBoard,
    nominate, cancelNomination, setNominator, stepNominator, setNomClock,
    undo, redo, canUndo, canRedo,
    restoredCount, dismissRestored, storageFailed, syncOk, setupDefaults,
    deleteBoardFor } = live;
  const [view, setView] = useState("rosters");
  const [showCliff, setShowCliff] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [showTeams, setShowTeams] = useState(false);
  /**
   * Read ONCE, at mount. It is a request carried by the click that got here,
   * not a piece of state the URL keeps re-asserting — and it is cleared, along
   * with the marker, the moment the user takes either way out of the screen.
   */
  const [setupAsked, setSetupAsked] = useState(setupRequestedInUrl);
  const leaveSetup = () => {
    setSetupAsked(false);
    // So a refresh, or the back button, does not put them back on the form.
    try {
      if (setupRequestedInUrl()) window.history.replaceState({}, "", "/live");
    } catch { /* no history API: the state above is what matters */ }
  };
  // Destructive-action state, both on the setup screen only. See the incident
  // note on startBoard's overwrite guard.
  const [confirmOverwrite, setConfirmOverwrite] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Bumped after a delete so the recovery scan re-runs — it is memoised on the
  // league, which does not change when a DIFFERENT league's board is removed.
  const [deletedTick, setDeletedTick] = useState(0);
  // Per device, in localStorage, deliberately NOT in board state — see
  // BoardScale.jsx. His laptop and the TV he casts to want different values.
  const [scale, setScale] = useBoardScale();
  // Same rule as the scale: per device, in localStorage, never in board state.
  // The kill switch for the highlight backdrop — see ClipToggle.jsx.
  const [clipsOn, setClipsOn] = useClipsEnabled();
  // 🔴 TV MODE. At the largest preset the page stops being a document with
  // margins and becomes a board: no max-width, minimal gutters, whole names on
  // the rows. Derived from the scale rather than a second control — one thing
  // to set on the device, and it is already the thing you set for a TV.
  const tv = isTvMode(scale);
  /**
   * 🔴 THE BATON, DECIDED ONCE. `useSoldFlash` is called HERE and nowhere else
   * on this surface: two instances means two 3.5s timers, and the banner
   * clearing at a different instant from the roster glow is a visibly dropped
   * baton. One flash, one `boardFocus`, handed to both consumers.
   */
  // `soldFlash`, not `sold` — `sold` is already the set of sold NAMES the entry
  // row excludes against, destructured above. Two different things.
  const soldFlash = useSoldFlash(board);
  const focus = boardFocus(board, soldFlash);

  /**
   * Boards with picks under OTHER league names. Only consulted when there is no
   * board here, but computed unconditionally because hooks must be.
   *
   * 🔴 RE-SCANNED WHEN THE ACTIVE LEAGUE CHANGES, and only then. It is a
   * localStorage sweep, so it must not run on every keystroke of the setup form
   * — and the answer cannot change while you sit on this screen, because the
   * only thing that would change it is starting a board, which leaves it.
   */
  const elsewhere = useMemo(
    () => (board ? [] : otherBoardsInProgress(activeLeagueName)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [board, activeLeagueName, deletedTick]
  );

  if (!board || setupAsked) {
    return (
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 28px 60px" }}>
        {/* 🔴 "MY BOARD IS GONE" vs "MY BOARD IS UNDER ANOTHER LEAGUE NAME" are
            indistinguishable from the operator's chair — and this screen
            suggests the first by inviting you to start a new one. Storage is
            league-scoped, so a board with picks in it can be sitting one name
            away while this renders blank. It says so; it does not switch.
            Renders NOTHING when there is nothing to recover, so a first-time
            user sees exactly what they saw before. See BoardsElsewhere. */}
        {/* 🔴 THE BOARD IS NOT HIDDEN, IT IS OFFERED. Somebody who came here
            to set up a draft and DOES have one running needs to see that in
            one line, at the top, with a way straight into it — the failure
            this replaces is a form that quietly implies the old board is
            gone. Creating a new one from below still goes through the
            overwrite confirmation, so the destructive path is unchanged. */}
        {board && setupAsked && (
          <div data-testid="board-in-progress" style={{
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            marginBottom: 18, padding: "9px 14px", borderRadius: theme.radius.md,
            background: theme.helpers.brandBg(0.08),
            border: `1px solid ${theme.colors.border.brandLight}`,
            fontSize: theme.fontSize.caption, color: theme.colors.text.muted,
          }}>
            <span>
              You have a board in progress for{" "}
              <strong style={{ color: theme.colors.text.primary }}>
                {board.leagueName || "this league"}
              </strong>:
            </span>
            <button
              type="button"
              data-testid="open-board-in-progress"
              onClick={leaveSetup}
              style={{
                background: "transparent", border: "none", padding: 0,
                color: theme.colors.brand.accent, fontFamily: "inherit",
                fontSize: theme.fontSize.caption, fontWeight: theme.fontWeight.bold,
                cursor: "pointer", textDecoration: "underline",
              }}
            >Open it</button>
          </div>
        )}
        <BoardsElsewhere
          boards={elsewhere}
          openable={(savedLeagues || []).map(l => l?.name).filter(Boolean)}
          onOpen={onSwitchLeague}
          onDelete={(b) => setConfirmDelete(b)}
        />
        {/* 🔴 startBoard REFUSES rather than overwriting — it returns
            { blocked: 'existing-board', … } when the target league already has
            picks. Aug 12 2026: writing straight through destroyed a five-pick
            board. The refusal is in the hook so every caller is covered; this
            is the caller turning it into a question. */}
        <LiveBoardSetup
          defaults={setupDefaults}
          onStart={(setup) => {
            const res = startBoard(setup);
            if (res && res.blocked === "existing-board") setConfirmOverwrite({ setup, ...res });
            // A board that actually started is the end of the request — without
            // this the new board would render behind the form that made it.
            else if (res && res.ok) leaveSetup();
            return res;
          }}
        />

        {confirmOverwrite && (
          <ConfirmDestroy
            title="That league already has a board"
            body={<>
              <strong>{confirmOverwrite.leagueName}</strong> has{" "}
              <strong>{confirmOverwrite.picks}</strong>{" "}
              {confirmOverwrite.picks === 1 ? "pick" : "picks"} entered. Starting a new
              board erases {confirmOverwrite.picks === 1 ? "it" : "them"}, and there is no undo.
            </>}
            confirmLabel="Erase and start new"
            onConfirm={() => {
              startBoard(confirmOverwrite.setup, { force: true });
              setConfirmOverwrite(null);
              // Same reason as the plain start above: the request is answered.
              leaveSetup();
            }}
            onCancel={() => setConfirmOverwrite(null)}
          />
        )}

        {confirmDelete && (
          <ConfirmDestroy
            title="Delete this board?"
            body={<>
              <strong>{confirmDelete.leagueName}</strong> has{" "}
              <strong>{confirmDelete.picks}</strong>{" "}
              {confirmDelete.picks === 1 ? "pick" : "picks"} entered. Deleting is permanent.
              There is no undo.
            </>}
            confirmLabel="Delete it"
            onConfirm={() => {
              deleteBoardFor(confirmDelete.league);
              setDeletedTick(t => t + 1);
              setConfirmDelete(null);
            }}
            onCancel={() => setConfirmDelete(null)}
          />
        )}
      </div>
    );
  }

  // `player,pos,price,team`. The manual fallback when the share link is not
  // available, and the on-ramp for real same-season auction data.
  const exportCsv = () => {
    const blob = new Blob([boardToCsv(board)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slugify(board.leagueName)}_auction.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const tab = (key, text) => (
    <button
      onClick={() => setView(key)}
      style={{
        background: view === key ? theme.colors.brand.orange : "transparent",
        color: view === key ? "#fff" : theme.colors.text.muted,
        border: `1px solid ${view === key ? theme.colors.brand.orange : theme.colors.border.default}`,
        borderRadius: theme.radius.md, padding: "5px 14px", cursor: "pointer",
        fontFamily: "inherit", fontSize: theme.fontSize.caption, fontWeight: theme.fontWeight.bold,
        letterSpacing: "0.04em",
      }}
    >{text}</button>
  );

  return (
    <div data-testid="live-page" style={{
      maxWidth: tv ? "none" : 1500, margin: "0 auto",
      padding: tv ? `6px 10px ${BAR_RESERVE}px` : `10px 20px ${BAR_RESERVE}px`,
    }}>
      {/* 🔴 THE BEAT, NOT A BACKGROUND. Fires on a player going up, lasts
             FLASH_MS, then the card keeps the clip on its own. Fixed and
             pointer-events: none, below the entry bar — Ben is typing the price
             while it plays. Same kill switch as the card. */}
      <NominationFlash board={board} enabled={clipsOn} />
      {/* ── THE BOARD. A3: one roster board, and it comes up. There is no
             compact team strip — every card already carries budget and max bid
             on its own header, so a summary row above it repeated the same two
             numbers in a worse form. ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        {tab("rosters", "ROSTERS")}
        {tab("board", "MY CHEATSHEET")}
        {/* 🔴 A VIEW OF board.sales, AND NOTHING PERSISTED FOR IT. See DraftLog. */}
        {tab("log", "DRAFT LOG")}
        <span style={{ fontSize: theme.fontSize.caption, color: theme.colors.text.faint, marginLeft: 6 }}>
          {board.leagueName} · ${board.budget} · {board.teamNames.length} teams · {totals.filled} sold
        </span>
        {/* 🔴 EVERYTHING RARE, BEHIND ONE BUTTON. Team names, Export CSV, Start
            over, the clips toggle, the nomination clock and the text size all
            used to be permanent rows. Six controls you touch once cost board on
            every screen for all 168 picks — and on a cast TV, which cannot be
            scrolled, board spent on setup controls is roster rows nobody can
            reach. See BoardMenu for the two documented rules this bends. */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {/* 🔴 THE TEXT SIZE, OUT OF THE MENU. The preset row is still in
              there and is still the way out of TV mode — this is the same
              scale, one rung at a time, put where somebody squinting at the
              board will actually find it. Two controls, one value: see the
              stepper's own header in BoardScale.jsx. */}
          <BoardScaleStepper scale={scale} onChange={setScale} testId="live-scale-stepper" />
          <BoardMenu
            board={board}
            syncOk={syncOk}
            onSetNominator={setNominator}
            onOpenTeamNames={() => setShowTeams(true)}
            onExportCsv={exportCsv}
            onStartOver={() => setConfirmReset(true)}
            onSetNomClock={setNomClock}
            clipsOn={clipsOn} onClipsChange={setClipsOn}
            scale={scale} onScaleChange={setScale}
            onLeave={onLeave}
          />
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          🔴 THE NOMINATION-HEIGHT RESERVE. At TV this block is the tallest it
          will ever be, always — so `gridTop` is a pure function of the viewport
          and NOTHING ON THE BOARD MOVES during the auction. Without it the grid
          jumps ~130px on every nomination and back on every sale: 168 times,
          at 20px type, on the one screen the whole room is watching.

          🔴 minHeight IS A FLOOR, SO THE CONTENT HAS TO ACTUALLY FIT. All three
          nomination states model under NOMINATION_RESERVE and tvMode.test.js
          asserts it per state — but a SIBLING inside the same column is added
          to the block's height rather than absorbed by it, and the two that can
          appear here are exactly the two that would push past it: countdown +
          waiting together before pick one, and the restore banner mid-draft on
          a resumed board. That is the same failure this exists to prevent,
          arriving through the door the fix opened.

          🔴 SO AT TV THE TRANSIENT NOTICES TAKE NO LAYOUT AT ALL. Same rule the
          roster cards already hold — nothing that appears and disappears may
          occupy layout space — applied one level up. NOT clipped: they render
          whole, over the top of the panel, for as long as they exist. Below TV
          they stay in flow exactly as they always have.
          ══════════════════════════════════════════════════════════════ */}
      <div style={{
        marginBottom: 12, position: "relative",
        minHeight: tv ? NOMINATION_RESERVE : undefined,
        display: "flex", flexDirection: "column",
      }}>
        <div
          data-testid="board-notices"
          style={tv
            ? { position: "absolute", top: 0, left: 0, right: 0, zIndex: 5 }
            : undefined}
        >
          {/* 🔴 RESTORE LOUDLY. Silent restore is how you get a duplicate entry. */}
          {restoredCount > 0 && (
            <div style={{
              background: "rgba(34,197,94,0.12)", border: `1px solid ${theme.colors.status.success}`,
              borderRadius: theme.radius.md, padding: "8px 14px", marginBottom: 10,
              display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
            }}>
              <span>✅</span>
              <span style={{ color: "#bbf7d0", fontSize: theme.fontSize.body, fontWeight: theme.fontWeight.bold }}>
                Resumed. {restoredCount} {restoredCount === 1 ? "pick" : "picks"} already entered.
              </span>
              <button onClick={dismissRestored} style={{
                marginLeft: "auto", background: "transparent", border: "none", color: "#bbf7d0",
                fontFamily: "inherit", fontSize: theme.fontSize.caption, cursor: "pointer", textDecoration: "underline",
              }}>dismiss</button>
            </div>
          )}

          {/* 🔴 THE ALARM, AT EVERY SCALE, TV INCLUDED — and it renders NOTHING
              when the feed is healthy. The copy affordance is in the board menu
              because nobody copies a URL off a television, but a publish
              failure means eleven people are holding a link that has stopped
              updating and the operator is the only person who can act on it.
              It is in here rather than above so that at TV it cannot move the
              grid: a feed dropping and recovering mid-draft is precisely the
              repeated appear/disappear the reserve exists for. */}
          <SyncAlarm syncOk={syncOk} />

          {/* Until pick one, and then never again — see DraftCountdown. Compact
              here: the operator has an entry row to get to, and he is the one
              person in the room who already knows when the draft starts. */}
          <DraftCountdown board={board} compact />
        </div>

        {/* No price, on the operator's screen either — see the board/assistant
            note in WatchPage.jsx. The block says WHO is up, or WHO WE ARE
            WAITING ON; what he is worth is on My Cheatsheet. */}
        {/* 🔴 THE BACKDROP IS THE OPERATOR'S ONLY. WatchPage renders this same
            component and does not pass showClips — 168 nominations across
            eleven phones on cellular is bandwidth nobody has measured, and a
            spectator feed that stalls on draft night costs far more than a TV
            without a highlight reel. */}
        <NominationBlock board={board} focus={focus} onCancel={cancelNomination} showClips={clipsOn} fill={tv} />
        {/* The clips toggle and the nomination clock used to sit in a row here.
            Both are in the menu now — the clock is set once before pick one,
            and the clips kill switch is the FIRST thing in the panel so it is
            still effectively one reach. */}
      </div>

      {view === "rosters" ? (
        <>
          {/* The text-size row used to sit here. It is in the menu on this
              surface only: its own reason for existing in-product is that
              "eleven people will not discover Ctrl +", which is an argument
              about SPECTATORS. /watch keeps it visible. */}
          <RosterBoard
            teams={liveTeams} rosterSlots={rosterSlots} scale={scale} fullNames={tv}
            sales={board.sales} onEditSale={setEditIndex}
            // DONE and Max $0 are answered off boardTotals, so the badge and the
            // entry row's "no roster spots left" warning cannot disagree.
            board={board}
            // One answer for both surfaces — see boardFocus. The grid does not
            // decide which seat is lit and does not know what a sold flash is.
            focus={focus}
          />

          {/* 🔴 STILL NEEDED — BEHIND THE ADVISORY SWITCH since Aug 26; see
              LIVE_BOARD_ADVISORY. The whole block goes, its marginTop with it,
              so the board's last card sits against the bottom of the page
              exactly as it would if this had never been written. `needs` is
              still computed by the hook and is unchanged. */}
          {advisory && needs.length > 0 && (
            <div style={{
              display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16,
              fontSize: theme.fontSize.caption, color: theme.colors.text.muted,
            }}>
              <span style={{
                textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: theme.fontWeight.bold,
                color: theme.colors.text.faint,
              }}>Still needed</span>
              {needs.map(n => (
                <span key={n.pos} style={{
                  padding: "2px 9px", borderRadius: 999,
                  background: theme.colors.posBg[n.pos], border: `1px solid ${theme.colors.posBorder[n.pos]}`,
                }}>
                  <strong style={{ color: theme.colors.pos[n.pos] }}>{n.pos}</strong>{" "}
                  <span style={{ color: theme.colors.text.secondary }}>{n.teamsNeedingOne} teams</span>
                  {n.teamsNeedingTwo > 0 && <span style={{ color: theme.colors.text.faint }}> · {n.teamsNeedingTwo} need 2+</span>}
                </span>
              ))}
            </div>
          )}

          {/* BEST LEFT is a between-nominations glance, not a while-typing one,
              so it is collapsed by default and sits below the board rather than
              pushing the first roster card down the page.
              🔴 THE TOGGLE GOES WITH THE PANEL — see LIVE_BOARD_ADVISORY. A
              disclosure button that opens nothing is worse than no button, and
              the whole block including its marginTop is what disappears, so
              there is no gap where it was. */}
          {advisory && (
            <div style={{ marginTop: 16 }}>
              <button
                onClick={() => setShowCliff(v => !v)}
                style={{
                  background: "transparent", border: `1px solid ${theme.colors.border.default}`,
                  borderRadius: theme.radius.md, color: theme.colors.text.muted, fontFamily: "inherit",
                  fontSize: theme.fontSize.caption, fontWeight: theme.fontWeight.bold,
                  padding: "5px 12px", cursor: "pointer", letterSpacing: "0.04em",
                }}
              >{showCliff ? "▾" : "▸"} BEST LEFT</button>
              {showCliff && (
                <div style={{ marginTop: 8 }}>
                  <TierCliff board={board} dynamicPrices={dynamicPrices} priceSettings={priceSettings} />
                </div>
              )}
            </div>
          )}
        </>
      ) : view === "log" ? (
        /* Every pick, in commit order, rendered from board.sales alone. Rows
           open the SAME SaleEditor the roster cards do — one handler, one
           editor, so a correction from either surface is the same edit. */
        <DraftLog board={board} onEditSale={setEditIndex} />
      ) : (
        /* BOARD is the user's own cheat sheet, crossing itself off as sales are
           entered — one state, one renderer, and printing works for free. */
        <LiveCheatSheet board={board} dynamicPrices={dynamicPrices} priceSettings={priceSettings} />
      )}

      {showTeams && (
        <TeamNamesEditor
          board={board} onRename={renameTeam} onSetUserTeam={setUserTeam}
          onClose={() => setShowTeams(false)}
        />
      )}

      {/* ── THE PINNED BAR. Fixed to the viewport bottom, above everything,
             and the last thing in the DOM so it is also last in tab order. ── */}
      <div data-testid="live-entry-bar" style={{
        position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 120,
        background: theme.colors.bg.deep,
        borderTop: `1px solid ${theme.colors.border.brand}`,
        boxShadow: "0 -8px 28px rgba(0,0,0,0.55)",
        padding: "8px 20px calc(8px + env(safe-area-inset-bottom, 0px))",
      }}>
        {/* Widens with the page — a 1500px row under a full-bleed board reads
            as a broken layout rather than as a deliberate margin. */}
        <div style={{ maxWidth: tv ? "none" : 1500, margin: "0 auto" }}>
          {/* 🔴 NO EMPTY SLOT WHERE RoomRead WAS — see LIVE_BOARD_ADVISORY.
              With the read gone the row justifies to the end instead of
              carrying a blank flex child, so the step arrows stay exactly where
              the operator's thumb already knows they are. */}
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            justifyContent: advisory ? undefined : "flex-end",
            marginBottom: 6, minWidth: 0,
          }}>
            {advisory && (
              <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                <RoomRead board={board} openSlots={totals.openSlots} dynamicPrices={dynamicPrices} compact />
              </div>
            )}
            <NominatorStep board={board} onStep={stepNominator} />
          </div>
          <LiveBoardEntry
            board={board} sold={sold} onCommit={commitSale}
            onUndo={undo} onRedo={redo} canUndo={canUndo} canRedo={canRedo}
            onNominate={nominate} storageFailed={storageFailed} dropUp
          />
        </div>
      </div>

      {/* Editing a row that is not the last one. Undo reaches only the most
          recent entry; over 192 picks an error noticed three picks later is
          guaranteed. */}
      {editIndex >= 0 && board.sales[editIndex] && (
        <SaleEditor
          sale={board.sales[editIndex]}
          teamNames={board.teamNames}
          onSave={(patch) => { editSale(editIndex, patch); setEditIndex(-1); }}
          onDelete={() => { deleteSale(editIndex); setEditIndex(-1); }}
          onCancel={() => setEditIndex(-1)}
        />
      )}

      {confirmReset && (
        <div style={{
          position: "fixed", inset: 0, background: theme.helpers.overlay(0.7), zIndex: 200,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div style={{
            background: theme.colors.extended.modalBg, border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.radius.xl, padding: 26, maxWidth: 420,
          }}>
            <div style={{
              fontSize: theme.fontSize.heading, fontWeight: theme.fontWeight.heavy,
              color: theme.colors.text.primary, marginBottom: 10,
            }}>Start over?</div>
            <div style={{ color: theme.colors.text.muted, fontSize: theme.fontSize.body, lineHeight: 1.55, marginBottom: 20 }}>
              This erases all {board.sales.length} entered {board.sales.length === 1 ? "pick" : "picks"} for
              {" "}{board.leagueName || "this board"}. Your team names are kept.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmReset(false)} style={{
                background: theme.colors.bg.raised, border: `1px solid ${theme.colors.border.default}`,
                borderRadius: theme.radius.md, color: theme.colors.text.primary, fontFamily: "inherit",
                fontSize: theme.fontSize.body, padding: "9px 16px", cursor: "pointer",
              }}>Cancel</button>
              <button onClick={() => { resetBoard(); setConfirmReset(false); }} style={{
                background: theme.colors.status.danger, border: "none", borderRadius: theme.radius.md,
                color: "#fff", fontFamily: "inherit", fontSize: theme.fontSize.body,
                fontWeight: theme.fontWeight.bold, padding: "9px 16px", cursor: "pointer",
              }}>Erase and start over</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
